import { trigger, transition, style, animate } from '@angular/animations';
import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { MeetingSummaryService } from 'app/modules/admin/services/meeting-summary.service';
import { SnackBarComponent, SnackBarData, SnackBarType } from 'app/shared/snackbar/snackbar.component';
import { Loggable } from 'app/utils/logging/loggable';
import SurveyAnswer from 'entity/SurveyAnswer';
import SurveyQuestion from 'entity/SurveyQuestion';
import User from 'entity/User';
import QuestionType from 'enums/QuestionType';

import { AnswerService } from '../services/answer.service';
import { GUEST_EMAIL_KEY } from 'app/modules/social/welcome/badging-questions/badging-questions.component';

interface DialogData {
  title: string;
  description: string;
  socialRoomSetId?: string;
  attendeeSurvey?: boolean;
}

@Component({
  selector: 'app-survey-card',
  templateUrl: './survey-card.component.html',
  styleUrls: ['./survey-card.component.scss'],
  animations: [
    trigger('carouselAnimation', [
      transition('void => *', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition('* => void', [animate('300ms', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyCardComponent extends Loggable implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<SurveyCardComponent>>(MatDialogRef);
  private router = inject(Router);
  private data = inject<DialogData>(MAT_DIALOG_DATA);
  private _answerService = inject(AnswerService);
  private store = inject(Store);
  private formBuilder = inject(FormBuilder);
  private meetingSumaryService = inject(MeetingSummaryService);
  private _snackBar = inject(MatSnackBar);

  public readonly title = signal('');
  public readonly currentSlide = signal(0);
  public readonly slides = signal<SurveyQuestion[][]>([]); // FIXME: find the real type
  private readonly isPoorPrompt = signal(false);

  public readonly question1 = signal<SurveyQuestion | undefined>(undefined);
  public readonly question2 = signal<SurveyQuestion | undefined>(undefined);
  public get questionType(): typeof QuestionType {
    return QuestionType;
  }
  public readonly aiSummaries = signal(false);
  public readonly attendeeSurvey = signal<boolean | undefined>(undefined);
  private readonly socialRoomSetId = signal<string | undefined>(undefined);
  protected readonly rating = signal(0);
  protected starArray = [1, 2, 3, 4, 5];
  public readonly surveyForm = signal<FormGroup | undefined>(undefined);
  public readonly isLoading = signal(false);

  protected readonly user = signal<User | undefined>(undefined);
  protected readonly isGuest = signal<boolean | undefined>(undefined);

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    const data = this.data;

    this.title.set(data.title);
    this.slides.set(this._answerService.splitQuestionList());
    this.attendeeSurvey.set(data.attendeeSurvey);
    this.socialRoomSetId.set(data.socialRoomSetId);

    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.ai_summaries),
      this.store.select(coreSelectors.getUser),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([aiSummaries, { isGuest, user }]) => {
        this.aiSummaries.set(aiSummaries);
        this.user.set(user);
        this.isGuest.set(isGuest);
        let initialValue = '';

        if (this.isGuest()) {
          const guestEmail = sessionStorage.getItem(GUEST_EMAIL_KEY);
          if (guestEmail) {
            initialValue = guestEmail;
          }
        }

        this.surveyForm.set(
          this.formBuilder.group({
            email: [initialValue, [Validators.required, Validators.email]],
            tellUs: [''],
          }),
        );
      });
  }

  public ngOnInit(): void {
    this.setQuestions(this.currentSlide());
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private setQuestions(index: number): void {
    this.title.set(this.slides()[index][0].title ?? '');
    this.question1.set(this.slides()[index][0]);
    this.question2.set(this.slides()[index][1]);
  }

  public onPreviousClick(): void {
    const previous = this.currentSlide() - 1;
    this.currentSlide.set(previous < 0 ? this.slides().length - 1 : previous);
    this.setQuestions(this.currentSlide());
  }

  public onNextClick(): void {
    const answers = this._answerService.getAnswers();
    let keyAnswer = '';

    for (const k of Object.keys(answers)) {
      const idx = Number(k);
      if (answers[idx].answer) {
        keyAnswer = answers[idx].answer ?? '';
      }
      this.submitAnswer(answers[idx]);
    }

    if (keyAnswer !== '') {
      const NumericAnswer = Number(keyAnswer);
      if (NumericAnswer < 3) {
        this.isPoorPrompt.set(true);
      }
    }

    this._answerService.clearAnswers();
    const next = this.currentSlide() + 1;
    this.currentSlide.set(next === this.slides().length ? 0 : next);
    this.setQuestions(this.currentSlide());
  }

  public async close(): Promise<void> {
    this.dialogRef.close();
    if (!this.attendeeSurvey() && !this.aiSummaries()) {
      await this.router.navigate(['/']);
    }
  }

  public async submitAndClose(): Promise<void> {
    const answers = this._answerService.getAnswers();
    let keyAnswer = '';

    for (const k of Object.keys(answers)) {
      const idx = Number(k);
      if (answers[idx].answer) {
        keyAnswer = answers[idx].answer ?? '';
      }
      this.submitAnswer(answers[idx]);
    }

    if (keyAnswer !== '') {
      const NumericAnswer = Number(keyAnswer);
      if (NumericAnswer < 3) {
        this.isPoorPrompt.set(true);
      }
    }
    await this.close();
  }

  protected async goToHomePage() {
    this.dialogRef.close();
    await this.router.navigate(['/']);
  }

  protected async submitAiSurvey() {
    if (!this.surveyForm()) {
      return;
    }

    if (!this.user() && !this.surveyForm()!.valid) {
      return;
    }

    if (!this.socialRoomSetId()) {
      this.error('Error while submit ai form, socialRoomSetId is: ', this.socialRoomSetId());
      return;
    }

    const email = this.isGuest() ? this.surveyForm()!.value.email : this.user()?.emailAddress;

    this.isLoading.set(true);
    await this.meetingSumaryService
      .registerForAttendeeSummary(this.socialRoomSetId()!, email)
      .then((res) => {
        void this.router.navigate([`web/admin/attendee-summary/${res}`]);
      })
      .catch((err) => {
        this.error('Something went wrong while registering for Attendee Summary, ', err);
        this.showSnackBar({ message: 'Something went wrong while getting your summary', type: 'error' });
        void this.router.navigate(['/']);
      })
      .finally(() => {
        this.isLoading.set(false);
        void this.submitAndClose();
      });
  }

  public readonly isLastSlide = computed(() => {
    return this.currentSlide() === this.slides().length - 1;
  });

  private submitAnswer(a: SurveyAnswer): void {
    this._answerService.submitAnswer(a).pipe(takeUntil(this.destroyed$)).subscribe();
  }

  protected changeRating(ratingId: number, surveyQuestionId?: number) {
    this.rating.set(ratingId);
    const answer: SurveyAnswer = { surveyQuestionId, skipped: false, answer: String(this.rating()) };
    this._answerService.trackAnswer(answer);
  }

  protected changeTellUsMore(surveyQuestionId?: number) {
    if (!this.surveyForm()?.get('tellUs')?.value) {
      return;
    }

    const answer: SurveyAnswer = { surveyQuestionId, skipped: false, answer: this.surveyForm()?.get('tellUs')?.value };
    this._answerService.trackAnswer(answer);
  }

  protected showIcon(ratingId: number) {
    if (this.rating() >= ratingId) {
      return 'star';
    }
    return 'star_border';
  }

  private showSnackBar({ message, type }: { type: SnackBarType; message: string }) {
    const _data: SnackBarData = {
      message: message,
      icon: type === 'success' ? 'check_circle' : 'cancel',
      isMatIcon: true,
      type: type,
      mode: 'light',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }
}
